/**
 * @module utils/columnSort/fromQB/buildFromQB
 * @description 未持久化 column_sort 时，编排 QB 各区块推导完整列排序展示列表
 */

import type { IColumnSortItem } from '@/pages/datalab/pages/Multidimensional/Analysis/types/chartSettings/fieldColumnSort';
import { buildCompareUserGroupItems } from './buildCompareUserGroup';
import type { ICompareTimeInput } from './buildDimensionSortItemsFromQB';
import { buildDimensionSortItemsFromQB } from './buildDimensionSortItemsFromQB';
import { buildMetricSortItemsFromQB } from './buildMetricSortItemsFromQB';
import type { IColumnPercentageItem } from '../untils/columnPercentage';
import type { IDimensionPercentageConfig } from '../../../../types/settings/dimensionPercentage';
import { dedupeColumnSortList } from '../untils/keys';
import { applyDimensionPctVirtualColumns } from '../syncFromQB/syncDimensionPercentage';

export interface IBuildColumnSortFromQBInput {
  columnDimensions: unknown[];
  columnMetrics: unknown[];
  columnFilters?: unknown[];
  columnUserFilters?: unknown[];
  /** 用户筛选总开关；false 时不生成对照组列（即使 columnUserFilters 仍有多组） */
  hasUserFilters?: boolean;
  columnPercentage?: IColumnPercentageItem[];
  dimensionPercentage?: IDimensionPercentageConfig;
  compareTime?: ICompareTimeInput;
}

/**
 * 未持久化 column_sort 时，由 QB 字段推导面板展示用的列排序列表。
 *
 * 编排方式：各子模块返回独立数组段，在此按固定顺序一次合并（不做中间 list 修补）。
 *
 * 列顺序约定（对齐旧 crud addColumnSort）：
 * 1. 对照组 → 2. 独立对比时间（若有）→ 3. 维度 → 4. 指标
 * 5. 每个源指标后：dim_pct 虚拟列（dimension_percentage，若有）→ % 虚拟列（column_percentage，若有）
 */
export function buildColumnSortFromQB(
  input: IBuildColumnSortFromQBInput,
): IColumnSortItem[] {
  // 段 1：对照组前缀（0 或 1 项）
  const userGroup = buildCompareUserGroupItems(
    input.columnUserFilters,
    input.hasUserFilters,
  );

  // 段 2 + 3：维度段；对比日期仅在筛选区时 leadingCompareTime 为独立列
  const { leadingCompareTime, dimensions, dimensionKeySet } =
    buildDimensionSortItemsFromQB(
      input.columnDimensions,
      input.compareTime,
      input.columnFilters,
    );

  // 段 4：指标段（含列占比 % 虚拟列）
  const metrics = buildMetricSortItemsFromQB(
    input.columnMetrics,
    dimensionKeySet,
    input.columnPercentage,
  );

  // 段 5：dim_pct 虚拟列紧跟主指标之后
  const withDimPct = applyDimensionPctVirtualColumns(
    dedupeColumnSortList([
      ...userGroup,
      ...leadingCompareTime,
      ...dimensions,
      ...metrics,
    ]),
    input.dimensionPercentage,
  );

  return dedupeColumnSortList(withDimPct);
}

export type { ICompareTimeInput } from './buildDimensionSortItemsFromQB';
